import { useState, useEffect, useRef } from 'react';
import { Box, FormControlLabel, Switch } from '@mui/material';
import DatePicker from './components/DatePicker';
import useInternalBooking from '../../../../hooks/useInternalBooking';
import { useBooking } from '../../../../context/bookingContext';
import { useAuth } from '../../../../context/authContext';
import LockIconWrapper from '../../../components/LockIconWrapper';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import { hasCapacityConfiguration } from '../../../../util/entertainmentUtils';

function DateSelect({ onDateSelect, onError }) {
  const {
    selectedEntertainment,
    selectedEntertainmentObject,
    selectedProduct,
    selectedResource,
    selectedDate,
    setSelectedDate,
    setPreviousSelectedDate,
    setMaxCapacity,
    setMinPersons,
    overbooking,
    setOverbooking,
    shouldWaitForResourceSelection,
  } = useBooking();
  const { checkRightAny } = useAuth();
  const [month, setMonth] = useState(() => {
    const date = dayjs(selectedDate);
    return date.isValid() ? date.month() + 1 : dayjs().month() + 1;
  });
  const [year, setYear] = useState(() => {
    const date = dayjs(selectedDate);
    return date.isValid() ? date.year() : dayjs().year();
  });
  const [daysData, setDaysData] = useState(null);
  const requestIdRef = useRef(0);

  const { getDaysOfMonth } = useInternalBooking();

  // Reset daysData and selectedDate when product changes to force reload
  useEffect(() => {
    setDaysData(null);
    if (selectedDate) {
      setSelectedDate(null);
    }
    setMaxCapacity(null);
  }, [selectedProduct]);

  useEffect(() => {
    if (!year || !month || !selectedEntertainment || !selectedProduct) return;

    const fetchDays = async () => {
      const requestId = ++requestIdRef.current;
      let isActive = true;
      try {
        // If product has resources and none is selected yet, don't make the call
        if (shouldWaitForResourceSelection(selectedProduct)) {
          return;
        }

        // Check if entertainment has valid capacity configuration
        if (selectedEntertainmentObject && !hasCapacityConfiguration(selectedEntertainmentObject)) {
          if (!isActive || requestId !== requestIdRef.current) return;
          setDaysData([]);
          return;
        }

        const data = await getDaysOfMonth(
          selectedEntertainment,
          selectedProduct,
          year,
          month - 1,
          overbooking,
          selectedResource
        );
        if (data) {
          if (!isActive || requestId !== requestIdRef.current) return;
          setDaysData(data.days);
          setMinPersons(data.minPersons);
          // if month has changed, set selected date to null
          if (selectedDate && month !== selectedDate.month() + 1) {
            setSelectedDate(null);
          }
        }
      } catch (error) {
        if (!isActive || requestId !== requestIdRef.current) return;
        toast.error('Fehler beim Laden der verfügbaren Tage.');
        onError?.(error);
      }
    };
    fetchDays();
    return () => {
      requestIdRef.current += 1;
    };
  }, [
    month,
    year,
    selectedEntertainment,
    selectedEntertainmentObject,
    selectedProduct,
    selectedResource,
    overbooking,
    shouldWaitForResourceSelection,
  ]);

  const handleDateChange = date => {
    if (!date) {
      setPreviousSelectedDate(selectedDate);
      setSelectedDate(null);
      setMaxCapacity(null);
      onDateSelect?.(null, null);
      return;
    }

    const dateObj = dayjs(date);

    // Preserve the previously selected date for change detection in dependent components
    setPreviousSelectedDate(selectedDate);
    setSelectedDate(dateObj);

    let selectedDayData = null;
    // Find the selected day's data and set max capacity
    if (daysData) {
      selectedDayData = daysData.find(day => day.day === dateObj.date());
      if (selectedDayData) {
        setMaxCapacity(selectedDayData.maxCapacity);
      }
    }

    if (dateObj.isValid()) {
      onDateSelect?.(dateObj, selectedDayData);
    }
  };

  const handleMonthChange = newDateObject => {
    setMonth(newDateObject.month);
    setYear(newDateObject.year);
    setDaysData(null);
  };

  // Update max capacity when daysData changes (e.g  aafter selecting a different resource)
  useEffect(() => {
    if (!daysData || !selectedDate) return;
    const selectedDayData = daysData.find(day => day.day === dayjs(selectedDate).date());
    if (selectedDayData) {
      setMaxCapacity(selectedDayData.maxCapacity);
    }
  }, [daysData, selectedDate]);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      {checkRightAny('internalBooking:special:overbook') && (
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, mt: -6 }}
        >
          {' '}
          {/* mt: -6 use cleaner solution */}
          <LockIconWrapper right='internalBooking:special:overbook'>
            <FormControlLabel
              control={
                <Switch
                  checked={overbooking}
                  onChange={e => setOverbooking(e.target.checked)}
                  color='success'
                />
              }
              label='Überbuchen erlauben'
            />
          </LockIconWrapper>
        </Box>
      )}
      <DatePicker
        month={month}
        year={year}
        selectedDate={selectedDate}
        data={daysData}
        onChange={handleDateChange}
        onNavigate={handleMonthChange}
      />
    </Box>
  );
}

export default DateSelect;
